import { useLang, track } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { Reveal, ImageReveal } from "@/components/Reveal";
import { SectionLabel, Marquee } from "@/components/Editorial";
import { PageHead, CTABand } from "@/components/Shared";
import { IMAGES, BSPORT } from "@/data/config";

export default function Classes() {
  const { t } = useLang();
  const c = t.classes.cards[0];
  return (
    <>
      <PageHead label={t.classes.label} title={t.classes.title} lead={t.classes.intro} />

      <section className="bg-petal" data-testid="classes-detail">
        <div className="mx-auto grid max-w-[1600px] items-stretch gap-0 md:grid-cols-2">
          <ImageReveal src={IMAGES.equipRow} alt="Reformers set with balls and magic circles" className="aspect-[4/5] md:aspect-auto md:h-full" />
          <div className="px-6 py-16 md:px-16 md:py-24">
            <div className="flex items-baseline justify-between border-b border-onyx/15 pb-6">
              <h2 className="font-serif text-5xl md:text-6xl">{c.name}</h2>
              <span className="label text-terracotta">{c.focus}</span>
            </div>
            {[["what", "whatA"], ["expect", "expectA"], ["who", "whoA"], ["duration", "durationA"]].map(([q, a], i) => (
              <Reveal key={q} delay={i * 0.05}>
                <div className="border-b border-onyx/10 py-6">
                  <p className="label text-terracotta">{c[q]}</p>
                  <p className="mt-3 font-sans text-base leading-relaxed text-onyx/75">{c[a]}</p>
                </div>
              </Reveal>
            ))}
            <Link
              to="/schedule"
              onClick={() => track("book_class_click", { source: "classes-detail" })}
              className="label mt-10 inline-block border border-onyx px-10 py-5 transition-colors duration-500 hover:bg-onyx hover:text-petal"
              data-testid="classes-view-schedule"
            >
              {t.common.viewSchedule} →
            </Link>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="bg-onyx text-petal" data-testid="classes-new">
        <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-6 py-24 md:grid-cols-2 md:px-12 md:py-36">
          <div>
            <SectionLabel light>{t.classes.newLabel}</SectionLabel>
            <Reveal><h2 className="mt-8 font-serif text-6xl leading-[0.95] md:text-7xl">{t.classes.newTitle}</h2></Reveal>
            <p className="mt-8 max-w-md font-sans text-lg leading-relaxed text-petal/80">{t.classes.newBody}</p>
          </div>
          <div className="md:text-right">
            <a
              href={BSPORT.INTRO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("intro_offer_click", { source: "classes" })}
              className="label inline-block border border-petal bg-petal px-10 py-5 text-onyx transition-colors duration-500 hover:bg-transparent hover:text-petal"
              data-testid="classes-intro-cta"
            >
              {t.classes.newCta}
            </a>
          </div>
        </div>
      </section>

      <section className="bg-onyx text-petal" data-testid="classes-private">
        <div className="mx-auto grid max-w-[1600px] items-stretch gap-0 md:grid-cols-2">
          <div className="px-6 py-16 md:px-16 md:py-24">
            <SectionLabel light>{t.classes.privateLabel}</SectionLabel>
            <Reveal>
              <h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-6xl">{t.classes.privateTitle}</h2>
            </Reveal>
            <p className="mt-6 max-w-md font-sans text-lg leading-relaxed text-petal/75">{t.classes.privateBody}</p>
            <div className="mt-10">
              {t.classes.privateOptions.map((o, i) => (
                <Reveal key={o.name} delay={i * 0.08}>
                  <div className="flex items-baseline justify-between gap-4 border-t border-petal/15 py-6" data-testid={`classes-private-option-${i}`}>
                    <div>
                      <p className="font-serif text-3xl">{o.name}</p>
                      <p className="mt-1 font-sans text-sm text-petal/55">{o.detail}</p>
                    </div>
                    <span className="font-serif text-3xl text-blush">{o.price}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Link
              to="/private"
              onClick={() => track("private_enquiry_click", { source: "classes" })}
              className="label mt-10 inline-block border border-petal bg-petal px-10 py-5 text-onyx transition-colors duration-500 hover:bg-transparent hover:text-petal"
              data-testid="classes-private-cta"
            >
              {t.classes.privateCta} →
            </Link>
          </div>
          <ImageReveal src={IMAGES.equipCarriage} alt="Private Reformer Pilates at Maison Core Amsterdam" className="aspect-[4/5] md:aspect-auto md:h-full md:min-h-[560px]" />
        </div>
      </section>

      <CTABand />
    </>
  );
}
